import { useState, useEffect } from 'react';
import { AxiosResponse } from 'axios';
import { ProgressIndicator } from '@veneer/core';
import HeaderBar from '../../components/HeaderBar';
import ValueProp from '../../components/ValueProp';
import Designer from '../../components/Designer';
import Resume from '../../components/Resume';
import InputsInfoBar from '../../components/InputsInfoBar';
import ModalError, { ModalErrorDetail } from '../../components/Modals/Error';
import { useExperimentsBaseUrl, useMediaclipKeepAliveUrl } from '../../helpers/envHooks';
import { ScreenInfo, useDataCollection } from '../../jarvis/dataCollection';
import { sendFinishedEvent } from '../../jarvis/eventService';
import { useInputParameters } from '../../helpers/useInputParameters';

import type { CseExperimentData } from '../../types';
import { useExperimentsWebHttpClient, usePhotobookApiWebHttpClient } from '../../helpers/useJarvisApiWebHttpClient';
import { LocalStorageHelper } from '../../helpers/localStorage';
import { PhotobookApi } from '../../helpers/apis/photobookApi';
import { ExperimentsApi } from '../../helpers/apis/experimentsApi';
import { WindowLocationHelper } from '../../helpers/windowLocation';

const DesignerPage = () => {
  const [userMediaclipToken, setUserMediaclipToken] = useState('');
  const [activeAppInstanceId, setActiveAppInstanceId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [projectExpirationDate, setProjectExpirationDate] = useState('');
  const [projectProductCategory, setProjectProductCategory] = useState('');
  const [projectHasLocalPhotos, setProjectHasLocalPhotos] = useState(false);
  const [projectAppInstanceId, setProjectAppInstanceId] = useState('');
  const [cseExperimentsData, setCseExperimentsData] = useState<CseExperimentData>();
  const [isTester, setIsTester] = useState(false);
  const [showDesigner, setShowDesigner] = useState(false);
  const [showValueProp, setShowValueProp] = useState(false);
  const [showResume, setShowResume] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errorDetail, setErrorDetail] = useState<ModalErrorDetail>();
  const dataCollection = useDataCollection();
  const inputs = useInputParameters();

  const experimentsBaseUrl = useExperimentsBaseUrl();

  const backButtonName = 'BackButton';
  const mediaclipKeepAliveUrl = useMediaclipKeepAliveUrl();

  const screenInfo: ScreenInfo = { name: 'Designer', activity: 'OrderPhotobook-v01' };

  const getWebAppVersion = () => process.env['REACT_APP_PACKAGE_VERSION'];

  const photobookApi = new PhotobookApi(usePhotobookApiWebHttpClient());
  const experimentsApi = new ExperimentsApi(useExperimentsWebHttpClient());

  useEffect(() => {
    console.log('Active App Instance ID:', inputs.appInstanceId);
    if (inputs.appInstanceId != null) {
      setActiveAppInstanceId(inputs.appInstanceId);
    }
    if (inputs.experimentId) {
      // Experiment data will be used for Value Prop template strings
      // and to determine the target of the 'Start Over' on the resume page.
      handleCseExperimentsData();
    } else {
      console.warn('Missing Experiments ID, using placeholder content.');
    }
    LocalStorageHelper.setLastPage('designer');
    if (inputs.projectId != null) {
      console.log('Validating access to requested project:', inputs.projectId);
      handleValidateProject(inputs.projectId);
      setShowValueProp(false);
      setShowResume(inputs.resume !== 'false');
    } else {
      createProject();
      setShowValueProp(true);
    }
  }, [inputs]);

  const createProject = async () => {
    try {
      const data = {
        appInstanceId: inputs.appInstanceId,
        experimentId: inputs.experimentId,
        productCategory: inputs.productCategory,
        locale: inputs.locale,
      };
      const response = await photobookApi.createProject(data);
      if (response.data) {
        handleProjectResponse(response);
      } else {
        throw new Error('Missing response from create project');
      }
    } catch (error) {
      setErrorDetail({
        context: 'Unable to create project',
        ...(error instanceof Error && { error: error }),
      });
      setHasError(true);
      console.log(error);
    }
  };

  const handleCseExperimentsData = async () => {
    try {
      if (inputs.experimentId == undefined) throw new Error('Missing experimentId');

      const response = await experimentsApi.getExperiment(inputs.experimentId);
      if (response.data && response.data.length !== 0) {
        console.log('Using CSE Experiments data from', experimentsBaseUrl);
        setCseExperimentsData(response.data[0] as CseExperimentData);
      } else {
        throw new Error('Missing response from CSE Experiments');
      }
    } catch (error) {
      setErrorDetail({
        context: 'Unable to collect experiments data',
        ...(error instanceof Error && { error: error }),
      });
    }
  };

  const handleValidateProject = async (projectIdToValidate: string) => {
    try {
      const response = await photobookApi.getProject(projectIdToValidate);
      if (response.data) {
        handleProjectResponse(response);
      } else {
        throw new Error('Missing response from get project');
      }
    } catch (error) {
      setErrorDetail({
        context: 'Unable to validate project',
        ...(error instanceof Error && { error: error }),
      });
      setHasError(true);
      console.log(error);
    }
  };

  const handleDeleteProject = async () => {
    try {
      await photobookApi.deleteProject(projectId);
      console.log('Project deleted:', projectId);
      return true;
    } catch (error) {
      setErrorDetail({
        context: 'Unable to delete project',
        ...(error instanceof Error && { error: error }),
      });
      setHasError(true);
      console.log(error);
    }
  };

  const handeResetUser = async () => {
    try {
      await photobookApi.deleteUser();
      console.log('User reseted');
      return true;
    } catch (error) {
      setErrorDetail({
        context: 'Unable to reset user',
        ...(error instanceof Error && { error: error }),
      });
      setHasError(true);
      console.log(error);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleProjectResponse = (response: AxiosResponse<any>) => {
    const {
      projectId: returnedProjectId,
      userMediaclipToken: returnedUserMediaclipToken,
      expiresAt,
      productCategory = 'photobook',
      appInstanceId,
      localPhotos,
    } = response.data;
    console.log('Project:', response.data);
    setProjectId(returnedProjectId);
    setProjectAppInstanceId(appInstanceId);
    setUserMediaclipToken(returnedUserMediaclipToken);
    setProjectExpirationDate(expiresAt);
    setProjectProductCategory(productCategory.toLowerCase());
    setProjectHasLocalPhotos(localPhotos);
    setIsTester(response.data.isTester);
    LocalStorageHelper.setProject(returnedProjectId, expiresAt, productCategory);
    setIsLoading(false);
    if (!showValueProp) {
      setShowDesigner(true);
    }
  };

  const sendClickEvent = (controlName: string) => {
    dataCollection.sendControlClickedEvent({ name: controlName, detail: getWebAppVersion() }, screenInfo);
  };

  const onClickBack = () => {
    sendClickEvent(backButtonName);
    sendFinishedEvent();
  };

  const onClickLaunchDesigner = () => {
    setShowDesigner(true);
    setShowValueProp(false);
    setShowResume(false);
  };

  const onClickResetDesigner = () => {
    handleDeleteProject();
    const tileLaunchUrl = cseExperimentsData?.tile?.serviceParams?.url;
    console.log('Reset will launch to tileLaunchUrl', tileLaunchUrl);
    if (
      tileLaunchUrl == undefined ||
      tileLaunchUrl.indexOf('https://static-dev.tropos-rnd.com/jarvis/react-photobook') > -1
    ) {
      // If our app
      console.info('Reset:: create a new mediaclip project');
      createProject();
      setShowValueProp(false);
      setShowResume(false);
      setShowDesigner(false);
    } else {
      // If experiment is configured as external app (like Strawman)
      console.info('Reset:: relaunch external experiment', tileLaunchUrl);
      WindowLocationHelper.href(tileLaunchUrl);
    }
  };

  const onClickResetUser = async () => {
    const reseted = await handeResetUser();
    if (reseted) sendFinishedEvent();
  };

  const categoryTitle: {
    [index: string]: string;
  } = {
    photobook: 'Photobooks',
    canvas: 'Canvas',
  };

  if (hasError) {
    return <ModalError state={hasError} detail={errorDetail as ModalErrorDetail} screenInfo={screenInfo} />;
  }

  if (isLoading) {
    return (
      <div className="spinner">
        <ProgressIndicator />
      </div>
    );
  }

  const valuePropView = (
    <ValueProp cseExperimentsData={cseExperimentsData} onClickLaunchDesigner={onClickLaunchDesigner} />
  );

  const resumeView = (
    <Resume
      userMediaclipToken={userMediaclipToken}
      activeAppInstanceId={activeAppInstanceId}
      projectId={projectId}
      projectCategory={projectProductCategory}
      projectExpirationDate={projectExpirationDate}
      projectAppInstanceId={projectAppInstanceId}
      projectHasLocalPhotos={projectHasLocalPhotos}
      isTester={isTester}
      onClickLaunchDesigner={onClickLaunchDesigner}
      onClickResetDesigner={onClickResetDesigner}
      onClickResetUser={onClickResetUser}
    />
  );

  const designerView = (
    <Designer
      projectId={projectId}
      userMediaclipToken={userMediaclipToken}
      keepAliveUrl={mediaclipKeepAliveUrl}
      dataCollection={dataCollection}
    />
  );

  let contentView: JSX.Element | null = null;
  if (showValueProp) {
    contentView = valuePropView;
  } else if (showResume) {
    contentView = resumeView;
  } else if (showDesigner) {
    contentView = designerView;
  }

  return (
    <>
      <HeaderBar handleBack={onClickBack} title={categoryTitle[projectProductCategory]} />
      {contentView}
      {isTester && <InputsInfoBar />}
    </>
  );
};

export default DesignerPage;
